// @ts-check

import { api } from '@/api'
import { toCamelCase } from '@/helpers/formatters'
import * as _ from 'lodash-es'

export function getExpenses(params, page) {
  const requestBody = { params: { ...params } }

  if (page) {
    requestBody.params = Object.assign(requestBody.params, { page })
  }

  if (_.has(requestBody.params, 'search')) {
    requestBody.params.search = encodeURI(requestBody.params.search)
  }

  return api.get(`expenses`, requestBody)
}

/**
 * Creates a new expense.
 * @param {*} expensePayload
 * @returns {Promise<ExpenseResource>}
 */
export const createExpense = async (expensePayload) => {
  const res = await api.post('expenses', expensePayload)
  return res.data
}

/**
 * Updates an existing expense.
 * @param {ExpenseResource['id']} expenseId
 * @param {*} expensePayload
 * @returns {Promise<ExpenseResource>}
 */
export const updateExpense = async (expenseId, expensePayload) => {
  const res = await api.put(`expenses/${expenseId}`, expensePayload)
  return res.data
}

/**
 * Returns expense with the given id.
 * @param {ExpenseResource['id']} expenseId
 * @returns {Promise<ExpenseResource>}
 */
export const getExpenseById = async (expenseId) => {
  const res = await api.get(`expenses/${expenseId}`)
  return res.data
}

/**
 * @param {{ limit?: number }} params
 * @returns {Promise<ExpenseResource[]>}
 */
export const getLinkableExpenses = async (params = {}) => {
  const res = await api.get(`expenses/linkable`, {
    params
  })
  return res.data
}

export const linkBankTransactionsToExpense = async (
  bankTransactions,
  expenseId
) =>
  api.put(`expenses/link`, {
    bank_transactions: bankTransactions,
    entry_id: expenseId
  })

/**
 * Returns suppliers from the API that matches the given name.
 *
 * @param {string} name Name to search the suppliers with.
 * @param {number} [limit=5] The number of suppliers to get.
 * @returns {Promise<any>}
 */
export const getSuppliersByName = async (name, limit = 5) => {
  const { data: suppliers } = await api.get('suppliers/expense-search', {
    params: {
      search: name,
      limit
    }
  })
  // Add any supplier data formatting here.
  return suppliers.map(({ business_id: businessId, ...other }) => ({
    ...other,
    businessId
  }))
}

/**
 * @param {ExpenseResource['id']} expenseId
 * @returns {Promise<EntryLogResource[]>}
 */
export const getExpenseActivityLog = async (expenseId) => {
  const { data: activityLog } = await api.get(
    `log/expenses/${expenseId}/history`
  )
  return activityLog
}

/**
 * @param {ExpenseResource['id']} expenseId
 * @param {LedgerAccountQueryParams} params
 * @returns {Promise<LedgerAccountResource[]>}
 */
export const getExpenseLedgerAccounts = async (expenseId, params = {}) => {
  const res = await api.get(`expenses/${expenseId}/ledger-accounts`, {
    params
  })
  return res.data
}

/**
 * @param {ExpenseResource['id']} expenseId
 * @returns {Promise<ExpenseResource>}
 */
export const copyExpense = async (expenseId) => {
  const res = await api.post(`expenses/${expenseId}/copy`)
  return res.data
}

/**
 * @param {ExpenseResource['id']} expenseId
 */
export const getPrefillablePaymentData = async (expenseId) => {
  const { data } = await api.get(`expenses/${expenseId}/outgoingpayments/setup`)
  return toCamelCase(data)
}

/**
 * @param {ExpenseResource['id']} expenseId
 */
export const deleteExpense = async (expenseId) =>
  api.delete(`expenses/${expenseId}`)

/**
 * @param {EntryQuestionTreeParams} [params={}]
 * @return {Promise<EntryQuestionTree>}
 */
export const getExpenseQuestionTree = async (params = {}) => {
  const { data: questionTree } = await api.get('expenses/question-tree', {
    params
  })
  return questionTree
}
